import { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';
import { fetchBotRead } from '@/services/botCalls';
import { ListRange, listRangeBounds } from '@/components/Hub/listRange';
import { chunk } from '@/pages/PatientListPage/services/pacQueueHydration';
import { RevenueLedgerEntry } from './ledgerRows';

/** The Revenue ledger for a range, through the bot (BAR-3026), plus each row's patient. */

const BOT_NAME = 'create-treatment-payment';

export interface RevenueLedgerState {
  entries: RevenueLedgerEntry[];
  /** The backend stopped at its cap; older rows in the range are missing. */
  hasMore: boolean;
  patientById: Map<string, Patient>;
  loading: boolean;
  error: string | undefined;
  /** Reads the range again, keeping the current rows on screen meanwhile. */
  reload: () => void;
}

const EMPTY: Omit<RevenueLedgerState, 'loading' | 'reload'> = {
  entries: [],
  hasMore: false,
  patientById: new Map(),
  error: undefined,
};

/** `range` is when the money moved; `recognized`, its recognition date (BAR-3027), unbounded by default. */
export function useRevenueLedger(range: ListRange, recognized: ListRange = {}): RevenueLedgerState {
  const medplum = useMedplum();
  const [state, setState] = useState<Omit<RevenueLedgerState, 'loading' | 'reload'>>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((key) => key + 1), []);
  const { start, end } = listRangeBounds(range);
  const { start: recognizedFrom, end: recognizedTo } = listRangeBounds(recognized);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    async function load(): Promise<void> {
      const { ledger } = await fetchBotRead<{ ledger: { entries: RevenueLedgerEntry[]; hasMore: boolean } }>(
        medplum,
        BOT_NAME,
        { operation: 'revenueLedger', from: start, to: end, recognizedFrom, recognizedTo }
      );
      const ids = [
        ...new Set(ledger.entries.map((entry) => entry.medplumPatientId).filter((id): id is string => Boolean(id))),
      ];
      const patients = (
        await Promise.all(
          chunk(ids).map((idChunk) => medplum.searchResources('Patient', { _id: idChunk.join(','), _count: '1000' }))
        )
      ).flat();

      if (!cancelled) {
        setState({
          entries: ledger.entries,
          hasMore: ledger.hasMore,
          patientById: new Map(patients.map((patient) => [patient.id as string, patient])),
          error: undefined,
        });
      }
    }

    load()
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({ ...EMPTY, error: error instanceof Error ? error.message : 'Could not load the ledger' });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [medplum, start, end, recognizedFrom, recognizedTo, reloadKey]);

  return { ...state, loading, reload };
}
